import { Trans } from '@lingui/macro'
import { Stack } from '@mui/material'
import { EnhancedDomain, Model } from 'algu-domain'

import {
  AttributeCardSelector,
  AttributeToggleSelector,
  ChildEntityCardSelector,
  MaterialSwatchSelector,
} from '@salsita-ai/platform-project-sdk/react'
import { PropertyContainer } from '@salsita-ai/platform-project-sdk/ui'

import { OakLegsColorSwatchLabel, SteelLegsColorSwatchLabel, TopColorSwatchLabel } from './SwatchLabels'

interface Props {
  id: Model.Table['id']
  $legs: EnhancedDomain.$Legs
  $top: EnhancedDomain.$Top
}

export const TablePropertyPage = ({ id, $legs, $top }: Props) => {
  return (
    <PropertyContainer.Group spacing="large" withDividers>
      <PropertyContainer.Section title={<Trans>Basics</Trans>}>
        <PropertyContainer.Group spacing="large" withDividers>
          <PropertyContainer.Item title={<Trans>Shape</Trans>}>
            <ChildEntityCardSelector entityId={id} instanceName="top" fixableViolations={['topShapeNotSupported']} />
          </PropertyContainer.Item>
          <PropertyContainer.Item title={<Trans>Material</Trans>}>
            <Stack spacing={2}>
              <AttributeCardSelector
                entityId={$top.id}
                instanceName="topMaterial"
                fixableViolations={[
                  'edgeFinishNotAvailableForThisMaterial',
                  'fenixVariantNotAvailableForThisMaterial',
                  'oakLegsAndTopColorNotMatching',
                  'thicknessNotAvailableForThisMaterial',
                  'topColorNotAvailableForThisMaterial',
                  'topFinishNotAvailableForThisMaterial',
                  'topMaterialNotSupported',
                ]}
              />
              {$top.material === Model.TopMaterial.Fenix && (
                <AttributeCardSelector
                  entityId={$top.id}
                  instanceName="fenixVariant"
                  fixableViolations={['thicknessNotAvailableForThisMaterial']}
                />
              )}
            </Stack>
          </PropertyContainer.Item>
          <PropertyContainer.Item title={<Trans>Legs</Trans>}>
            <ChildEntityCardSelector entityId={id} instanceName="legs" />
          </PropertyContainer.Item>
          {EnhancedDomain.$LegsWithOffset.is($legs) && (
            <PropertyContainer.Item title={<Trans>Leg offset from end</Trans>}>
              <AttributeToggleSelector entityId={$legs.id} instanceName="legOffset" />
            </PropertyContainer.Item>
          )}
          {EnhancedDomain.$LegsWithThickness.is($legs) && (
            <PropertyContainer.Item title={<Trans>Leg thickness</Trans>}>
              <AttributeToggleSelector entityId={$legs.id} instanceName="legThickness" />
            </PropertyContainer.Item>
          )}
        </PropertyContainer.Group>
      </PropertyContainer.Section>
      <PropertyContainer.Section title={<Trans>Measurements</Trans>}>
        <PropertyContainer.Group spacing="large" withDividers>
          {EnhancedDomain.$TopCircle.is($top) ? (
            <PropertyContainer.Item title={<Trans>Diameter</Trans>}>
              <AttributeToggleSelector entityId={$top.id} instanceName="diameter" />
            </PropertyContainer.Item>
          ) : (
            <>
              <PropertyContainer.Item title={<Trans>Length</Trans>}>
                <AttributeToggleSelector entityId={$top.id} instanceName="length" />
              </PropertyContainer.Item>
              <PropertyContainer.Item title={<Trans>Width</Trans>}>
                <AttributeToggleSelector entityId={$top.id} instanceName="width" />
              </PropertyContainer.Item>
            </>
          )}
          <PropertyContainer.Item title={<Trans>Thickness</Trans>}>
            <AttributeToggleSelector entityId={$top.id} instanceName="thickness" />
          </PropertyContainer.Item>
          <PropertyContainer.Item title={<Trans>Height</Trans>}>
            <AttributeToggleSelector entityId={id} instanceName="height" />
          </PropertyContainer.Item>
        </PropertyContainer.Group>
      </PropertyContainer.Section>
      <PropertyContainer.Section title={<Trans>Table top</Trans>}>
        <PropertyContainer.Group spacing="large" withDividers>
          {$top.material === Model.TopMaterial.SolidOak && (
            <PropertyContainer.Item title={<Trans>Top Finish</Trans>}>
              <AttributeCardSelector entityId={$top.id} instanceName="topFinish" />
            </PropertyContainer.Item>
          )}
          <PropertyContainer.Item
            title={
              Model.ColorOak.is($top.color) && EnhancedDomain.$OakLegs.is($legs) ? (
                <Trans>Top and Legs Colour</Trans>
              ) : (
                <Trans>Top Colour</Trans>
              )
            }
          >
            <TopColorSwatchLabel swatchKey={$top.color} />
            <MaterialSwatchSelector
              entityId={$top.id}
              fixableViolations={['oakLegsAndTopColorNotMatching']}
              instanceName="topColor"
            />
          </PropertyContainer.Item>
          <PropertyContainer.Item title={<Trans>Edge Finish</Trans>}>
            <AttributeCardSelector entityId={$top.id} instanceName="edgeFinish" />
          </PropertyContainer.Item>
        </PropertyContainer.Group>
      </PropertyContainer.Section>
      {EnhancedDomain.$OakLegs.is($legs) && $top.material !== Model.TopMaterial.SolidOak && (
        <PropertyContainer.Section title={<Trans>Legs</Trans>}>
          <PropertyContainer.Group spacing="large" withDividers>
            <PropertyContainer.Item title={<Trans>Oak Legs Colour</Trans>}>
              <OakLegsColorSwatchLabel swatchKey={$legs.color} />
              <MaterialSwatchSelector entityId={$legs.id} instanceName="legsColor" />
            </PropertyContainer.Item>
          </PropertyContainer.Group>
        </PropertyContainer.Section>
      )}
      {EnhancedDomain.$SteelLegs.is($legs) && (
        <PropertyContainer.Section title={<Trans>Legs</Trans>}>
          <PropertyContainer.Group spacing="large" withDividers>
            <PropertyContainer.Item title={<Trans>Legs Colour</Trans>}>
              <SteelLegsColorSwatchLabel swatchKey={$legs.color} />
              <MaterialSwatchSelector entityId={$legs.id} instanceName="legsColor" />
            </PropertyContainer.Item>
          </PropertyContainer.Group>
        </PropertyContainer.Section>
      )}
    </PropertyContainer.Group>
  )
}
